import { useEffect } from 'react';
import { recoverCart } from '@nexacore/api-clients/fnb/public';
import { useQueryClient } from '@tanstack/react-query';
import { responseErrorCode } from '@/features/entry/entryErrors';
import { earliestCartExpiry, useCartWorkflowStore } from '@/state/cartWorkflowStore';
import { isFutureCapabilityExpiry, useCustomerSessionStore } from '@/state/customerSessionStore';
import { cartQueryKey } from './cartApi';
import type { PublicCart } from './types';

/**
 * Redeems only the persisted opaque recovery grant. It never reads Cart facts from storage and
 * intentionally treats all invalid/revoked grants as a silent empty-cart recovery failure.
 */
export function CartRecoveryGuard() {
  const pending = useCartWorkflowStore((state) => state.pendingRecovery);
  const consume = useCartWorkflowStore((state) => state.consumePendingRecovery);
  const setRecovery = useCartWorkflowStore((state) => state.setRecovery);
  const markFailed = useCartWorkflowStore((state) => state.markRecoveryFailed);
  const setSession = useCustomerSessionStore((state) => state.setSession);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!pending) return;
    const record = consume();
    if (!record) return;
    void recoverCart({ recoveryGrant: record.recoveryGrant })
      .then((response) => {
        const code = responseErrorCode(response);
        const session = response.data?.session;
        const cart = response.data?.cart as PublicCart | undefined;
        const grant = cart?.recovery;
        const recoveryExpiresAt = earliestCartExpiry(cart?.expiresAt, grant?.expiresAt);
        if (
          !response.succeeded ||
          code ||
          !session?.token ||
          !isFutureCapabilityExpiry(session.expiresAt) ||
          !session.store ||
          !cart?.id ||
          !cart.proofToken ||
          !grant?.grant ||
          !recoveryExpiresAt ||
          !session.store.storeCode ||
          session.store.storeCode.toUpperCase() !== record.storeCode.toUpperCase()
        ) {
          markFailed();
          return;
        }
        setSession(session.token, session.expiresAt, session.store);
        setRecovery({
          cartId: cart.id,
          cartProof: cart.proofToken,
          recoveryGrant: grant.grant,
          storeCode: session.store.storeCode,
          expiresAt: recoveryExpiresAt,
        });
        queryClient.setQueryData(
          cartQueryKey(session.token, session.store.storeCode, cart.id),
          cart
        );
      })
      .catch(() => {
        markFailed();
      });
  }, [consume, markFailed, pending, queryClient, setRecovery, setSession]);

  return null;
}
